/**
 * Roteamento principal — PPM.PAGO SPA (spec §11.9)
 *
 * Padrões:
 *   - Code-splitting por rota: cada rota usa React.lazy() + Suspense.
 *   - Code-splitting por permissão: bundles de módulos restritos (admin, financeiro, crédito)
 *     são carregados APENAS quando o usuário tem a role necessária.
 *     Isso evita que código sensível seja baixado por usuários sem acesso.
 *   - Error Boundary por rota (RouteErrorBoundary).
 *   - Route Guards apenas para UX; autorização real é do backend Go.
 *
 * Estrutura de rotas:
 *   /                   → primeiro item do menu que o perfil enxerga (se autenticado) ou /login
 *   /login              → LoginPage (pública)
 *   /definir-senha      → DefinirSenhaPage (pública: primeiro acesso, recuperação e senha expirada — #827)
 *   /dashboard          → DashboardPage (requer auth + permissão do item do menu)
 *   /associados         → AssociadosPage (requer auth + associado:listar)
 *   /bens               → BensPage (requer auth + bem:listar)
 *   /financeiro/*       → FinanceiroLayout (abas: Boletos/Plano de contas/
 *                         Centros de custo/Razão; cada aba com a própria permissão)
 *   /rateio             → RateioPage (requer auth + rateio:ver)
 *   /sem-acesso         → ForbiddenPage
 *   *                   → 404
 */

import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  ScrollRestoration,
  RouterProvider,
  Navigate,
  useParams,
  type RouteObject,
} from 'react-router-dom'
import { RequireAuth, RequirePermission, RequireRole } from './route-guard'
import { permissoesDaRota } from '@/features/shared/nav-visibilidade'
import { navItems } from '@/features/shared/nav-items'

const modulosDeConfig = navItems.find((item) => item.to === '/config')?.children ?? []
import { permissaoDaAba, permissoesDaAba } from '@/features/financeiro/abas-financeiro'
import { permissaoDaAba as permissaoDaAbaRede } from '@/features/rede/abas-rede'
import { TenantProvider } from '@/features/shared/tenant'
import { PorteiraDocumentos } from '@/features/documentos/porteira-documentos'
import { RequireMfa } from './require-mfa'
import { RequireSenhaDefinida } from './require-senha-definida'
import { RouteErrorBoundary } from './error-boundary'
import { rotasDoPortal } from '@/features/portal/portal-rotas'
import { RedirectComQuery } from './redirect-com-query'

// ─── Lazy imports (code-splitting por rota) ───────────────────────────────────

const LoginPage = lazy(() =>
  import('@/features/auth/login-page').then((m) => ({ default: m.LoginPage })),
)
const DefinirSenhaPage = lazy(() =>
  import('@/features/auth/definir-senha-page').then((m) => ({ default: m.DefinirSenhaPage })),
)
const CaptacaoPublicaPage = lazy(() =>
  import('@/features/captacao/captacao-publica-page').then((m) => ({ default: m.CaptacaoPublicaPage })),
)
// Guia de fotos de vistoria (mig 000093, Fase 3) — lado PÚBLICO, token na
// URL, SEM sessão (mesmo padrão de CaptacaoPublicaPage acima).
const VistoriaPublicaPage = lazy(() =>
  import('@/features/vistoria/vistoria-publica-page').then((m) => ({ default: m.VistoriaPublicaPage })),
)
// Assinatura eletrônica (PR3/5) — jornada pública do CONTRATO DE ADESÃO, token
// na URL, SEM sessão (mesmo padrão de VistoriaPublicaPage acima).
const AssinarPage = lazy(() =>
  import('@/features/assinatura/assinar-page').then((m) => ({ default: m.AssinarPage })),
)
// Compartilhamento de proposta (paridade PowerCRM, 02/09/2026) — jornada
// pública do LEAD conferindo/escolhendo a proposta, token na URL, SEM sessão
// (mesmo padrão de AssinarPage acima).
const PropostaPublicaPage = lazy(() =>
  import('@/features/catalogo/proposta-publica-page').then((m) => ({ default: m.PropostaPublicaPage })),
)
// /simulacao/:token: foto da simulação do CRM compartilhada pelo consultor, sem sessão.
const SimulacaoPublicaPage = lazy(() => import('@/features/simulador/simulacao-publica-page'))
// /comparar/:token: comparação de PLANOS do catálogo (refinamento 02/09/2026),
// mesma casca pública sem sessão.
const ComparacaoPublicaPage = lazy(() =>
  import('@/features/catalogo/comparacao-publica-page').then((m) => ({ default: m.ComparacaoPublicaPage })),
)
// Checkout público do Clube de Benefícios (CLUBE-16) — /clube/:slug, sem sessão
// (o visitante é anônimo). Render + submissão via endpoints públicos (skipAuth).
// A rota de retorno do pagamento é ESTÁTICA (/clube/retorno) e, por ser mais
// específica, vence o :slug no ranking do router — slugs são opacos/aleatórios,
// não colidem com a palavra reservada.
const ClubeCheckoutPage = lazy(() =>
  import('@/features/clube-publico/clube-checkout-page').then((m) => ({ default: m.ClubeCheckoutPage })),
)
const ClubeRetornoPage = lazy(() =>
  import('@/features/clube-publico/clube-retorno-page').then((m) => ({
    default: m.ClubeRetornoPage,
  })),
)
// Área do PRESTADOR (CLUBE-24) — realm próprio (cookie `ppm_clube_prestador`),
// fora do RequireAuth e do AppShell de staff. Mobile-first (390 px). Lazy como
// toda rota: a feature fica FORA do chunk `index`.
const ClubePrestadorEntrarPage = lazy(() =>
  import('@/features/clube-prestador/clube-prestador-entrar-page').then((m) => ({
    default: m.ClubePrestadorEntrarPage,
  })),
)
const ClubePrestadorPrimeiroAcessoPage = lazy(() =>
  import('@/features/clube-prestador/clube-prestador-primeiro-acesso-page').then((m) => ({
    default: m.ClubePrestadorPrimeiroAcessoPage,
  })),
)
const ClubePrestadorGuard = lazy(() =>
  import('@/features/clube-prestador/clube-prestador-guard').then((m) => ({
    default: m.ClubePrestadorGuard,
  })),
)
const ClubePrestadorAgendaPage = lazy(() =>
  import('@/features/clube-prestador/clube-prestador-agenda-page').then((m) => ({
    default: m.ClubePrestadorAgendaPage,
  })),
)
const ClubePrestadorChamadoPage = lazy(() =>
  import('@/features/clube-prestador/clube-prestador-chamado-page').then((m) => ({
    default: m.ClubePrestadorChamadoPage,
  })),
)

// Portal do PARCEIRO (CLUBE-30) — realm próprio (cookie `ppm_clube_parceiro`),
// fora do RequireAuth e do AppShell de staff. Mobile-first (390 px). Lazy: a
// feature fica FORA do chunk `index`.
const ClubeParceiroEntrarPage = lazy(() =>
  import('@/features/clube-parceiro/clube-parceiro-entrar-page').then((m) => ({
    default: m.ClubeParceiroEntrarPage,
  })),
)
const ClubeParceiroPrimeiroAcessoPage = lazy(() =>
  import('@/features/clube-parceiro/clube-parceiro-primeiro-acesso-page').then((m) => ({
    default: m.ClubeParceiroPrimeiroAcessoPage,
  })),
)
const ClubeParceiroGuard = lazy(() =>
  import('@/features/clube-parceiro/clube-parceiro-guard').then((m) => ({
    default: m.ClubeParceiroGuard,
  })),
)
const ClubeParceiroValidarPage = lazy(() =>
  import('@/features/clube-parceiro/clube-parceiro-validar-page').then((m) => ({
    default: m.ClubeParceiroValidarPage,
  })),
)
const ClubeParceiroHistoricoPage = lazy(() =>
  import('@/features/clube-parceiro/clube-parceiro-historico-page').then((m) => ({
    default: m.ClubeParceiroHistoricoPage,
  })),
)
const ClubeParceiroOfertasPage = lazy(() =>
  import('@/features/clube-parceiro/clube-parceiro-ofertas-page').then((m) => ({
    default: m.ClubeParceiroOfertasPage,
  })),
)

// Área do MEMBRO EXTERNO (CLUBE-37) — realm próprio (cookie `ppm_clube_membro`),
// fora do RequireAuth e do AppShell de staff. É a mesma experiência do clube do
// portal do associado, para quem comprou um plano pelo checkout público sem ser
// associado. Lazy: a feature (e as telas compartilhadas do clube) ficam FORA do
// chunk `index`.
const ClubeMembroEntrarPage = lazy(() =>
  import('@/features/clube-membro/clube-membro-entrar-page').then((m) => ({
    default: m.ClubeMembroEntrarPage,
  })),
)
const ClubeMembroPrimeiroAcessoPage = lazy(() =>
  import('@/features/clube-membro/clube-membro-primeiro-acesso-page').then((m) => ({
    default: m.ClubeMembroPrimeiroAcessoPage,
  })),
)
const ClubeMembroGuard = lazy(() =>
  import('@/features/clube-membro/clube-membro-guard').then((m) => ({
    default: m.ClubeMembroGuard,
  })),
)
const ClubeMinhaConta = lazy(() =>
  import('@/features/clube-membro/clube-minha-conta').then((m) => ({
    default: m.ClubeMinhaConta,
  })),
)

const DashboardPage = lazy(() =>
  import('@/features/dashboard/dashboard-page').then((m) => ({
    default: m.DashboardPage,
  })),
)

const AppShell = lazy(() =>
  import('@/features/app-shell').then((m) => ({ default: m.AppShell })),
)

const AssociadosPage = lazy(() =>
  import('@/features/associados/associados-page').then((m) => ({
    default: m.AssociadosPage,
  })),
)
// Detalhe do associado = Visão 360º com navegação interna por abas (Dados
// cadastrais/Bens/Financeiro/Eventos/Beneficiários/Background), mesmo padrão
// de layout+Outlet do módulo Financeiro — ver associado-detail-layout.tsx.
const AssociadoDetailLayout = lazy(() =>
  import('@/features/associados/associado-detail-layout').then((m) => ({
    default: m.AssociadoDetailLayout,
  })),
)
const AssociadoDadosTab = lazy(() =>
  import('@/features/associados/associado-dados-tab').then((m) => ({ default: m.AssociadoDadosTab })),
)
const AssociadoBensTab = lazy(() =>
  import('@/features/associados/associado-bens-tab').then((m) => ({ default: m.AssociadoBensTab })),
)
const AssociadoFinanceiroTab = lazy(() =>
  import('@/features/associados/associado-financeiro-tab').then((m) => ({ default: m.AssociadoFinanceiroTab })),
)
const AssociadoEventosTab = lazy(() =>
  import('@/features/associados/associado-eventos-tab').then((m) => ({ default: m.AssociadoEventosTab })),
)
const AssociadoBeneficiariosTab = lazy(() =>
  import('@/features/associados/associado-beneficiarios-tab').then((m) => ({ default: m.AssociadoBeneficiariosTab })),
)
const AssociadoBackgroundTab = lazy(() =>
  import('@/features/associados/associado-background-tab').then((m) => ({ default: m.AssociadoBackgroundTab })),
)

const BeneficiariosPage = lazy(() =>
  import('@/features/beneficiarios/beneficiarios-page').then((m) => ({
    default: m.BeneficiariosPage,
  })),
)
const BeneficiarioDetailPage = lazy(() =>
  import('@/features/beneficiarios/beneficiario-detail-page').then((m) => ({
    default: m.BeneficiarioDetailPage,
  })),
)
const BeneficiarioCreatePage = lazy(() =>
  import('@/features/beneficiarios/beneficiario-create-page').then((m) => ({
    default: m.BeneficiarioCreatePage,
  })),
)

const BensPage = lazy(() =>
  import('@/features/bens/bens-page').then((m) => ({ default: m.BensPage })),
)
const BemCreatePage = lazy(() =>
  import('@/features/bens/bem-create-page').then((m) => ({ default: m.BemCreatePage })),
)
const BemDetailPage = lazy(() =>
  import('@/features/bens/bem-detail-page').then((m) => ({ default: m.BemDetailPage })),
)

const AssociadoCreatePage = lazy(() =>
  import('@/features/associados/associado-create-page').then((m) => ({
    default: m.AssociadoCreatePage,
  })),
)

// Central de Boletos (docs/design_central_boletos.md): aba "Boletos" de /financeiro,
// carregada por `FinanceiroIndex` (abaixo).

// Financeiro é um módulo único (Boletos + Financeiro Corporativo) com
// navegação interna por abas — ver financeiro-layout.tsx.
const FinanceiroLayout = lazy(() =>
  import('@/features/financeiro/financeiro-layout').then((m) => ({
    default: m.FinanceiroLayout,
  })),
)

// Rede Comercial (REDE-09) — módulo com abas (Minha rede · Consultores ·
// Permissões), tudo lazy: só o item de menu e a flag moram no chunk inicial.
const RastreadorCadastroPage = lazy(() =>
  import('@/features/rastreador/cadastro-page').then((m) => ({ default: m.RastreadorCadastroPage })),
)
const RastreadorKanbanPage = lazy(() =>
  import('@/features/rastreador/kanban-page').then((m) => ({ default: m.RastreadorKanbanPage })),
)
const RastreadorNovoPage = lazy(() =>
  import('@/features/rastreador/novo-page').then((m) => ({ default: m.RastreadorNovoPage })),
)
const RastreadorAprovarPage = lazy(() =>
  import('@/features/rastreador/aprovar-page').then((m) => ({ default: m.RastreadorAprovarPage })),
)
const RastreadorTvPage = lazy(() =>
  import('@/features/rastreador/tv-page').then((m) => ({ default: m.RastreadorTvPage })),
)
const RastreadorRelatorioPage = lazy(() =>
  import('@/features/rastreador/relatorio-page').then((m) => ({ default: m.RastreadorRelatorioPage })),
)
const RastreadorConfiguracaoPage = lazy(() =>
  import('@/features/rastreador/configuracao-page').then((m) => ({ default: m.RastreadorConfiguracaoPage })),
)
const RedeLayout = lazy(() =>
  import('@/features/rede/rede-layout').then((m) => ({ default: m.RedeLayout })),
)
const MinhaRedePage = lazy(() =>
  import('@/features/rede/minha-rede-page').then((m) => ({ default: m.MinhaRedePage })),
)
const ConsultoresPage = lazy(() =>
  import('@/features/rede/consultores-page').then((m) => ({ default: m.ConsultoresPage })),
)
const MetasComissaoPage = lazy(() =>
  import('@/features/rede/metas-comissao-page').then((m) => ({ default: m.MetasComissaoPage })),
)
const CooperativasRedePage = lazy(() =>
  import('@/features/rede/cooperativas-page').then((m) => ({ default: m.CooperativasPage })),
)
const IndicadoresRedePage = lazy(() =>
  import('@/features/rede/indicadores-page').then((m) => ({ default: m.IndicadoresPage })),
)
const FechamentoRedePage = lazy(() =>
  import('@/features/rede/fechamento-page').then((m) => ({ default: m.FechamentoPage })),
)
const ExtratosRedePage = lazy(() =>
  import('@/features/rede/extratos-page').then((m) => ({ default: m.ExtratosPage })),
)
const PermissoesRedePage = lazy(() =>
  import('@/features/rede/permissoes-page').then((m) => ({ default: m.PermissoesRedePage })),
)

const RateioPage = lazy(() =>
  import('@/features/rateio/rateio-page').then((m) => ({ default: m.RateioPage })),
)
const AbrirFechamentoPage = lazy(() =>
  import('@/features/rateio/abrir-fechamento-page').then((m) => ({ default: m.AbrirFechamentoPage })),
)

// Financeiro Corporativo (WFC-1) — razão operacional. Abas de /financeiro.
const PlanoContasPage = lazy(() =>
  import('@/features/financeiro-corp/plano-contas-page').then((m) => ({ default: m.PlanoContasPage })),
)
const CentrosCustoPage = lazy(() =>
  import('@/features/financeiro-corp/centros-custo-page').then((m) => ({ default: m.CentrosCustoPage })),
)
const RazaoPage = lazy(() =>
  import('@/features/financeiro-corp/razao-page').then((m) => ({ default: m.RazaoPage })),
)

// Contas a Pagar (WFC-2) — fornecedor/OC (three-way match)/título/pagamento/
// contrato recorrente. Mesmas abas do módulo Financeiro (financeiro-layout.tsx).
const FornecedoresPage = lazy(() =>
  import('@/features/compras/fornecedores-page').then((m) => ({ default: m.FornecedoresPage })),
)
const FornecedorNovoPage = lazy(() =>
  import('@/features/compras/fornecedor-novo-page').then((m) => ({ default: m.FornecedorNovoPage })),
)
const FornecedorPage = lazy(() =>
  import('@/features/compras/fornecedor-page').then((m) => ({ default: m.FornecedorPage })),
)
const OrdensCompraPage = lazy(() =>
  import('@/features/financeiro-corp/ordens-compra-page').then((m) => ({ default: m.OrdensCompraPage })),
)
const TitulosPagarPage = lazy(() =>
  import('@/features/financeiro-corp/titulos-pagar-page').then((m) => ({ default: m.TitulosPagarPage })),
)
// Comissões de parceiros (#232) — cadastro de pagamento do parceiro + fila de
// comissões que vira título em Contas a Pagar. Mora no Financeiro, não no CRM:
// quem tem comissao:pagar é o analista financeiro, sem permissão de crm:*.
const ComissoesParceirosPage = lazy(() =>
  import('@/features/financeiro-corp/comissoes-parceiros-page').then((m) => ({ default: m.ComissoesParceirosPage })),
)

const ContratosRecorrentesPage = lazy(() =>
  import('@/features/financeiro-corp/contratos-recorrentes-page').then((m) => ({ default: m.ContratosRecorrentesPage })),
)

// Contas a Receber (FC-3 "Parte A") — mesma aba do módulo Financeiro.
const RecebiveisPage = lazy(() =>
  import('@/features/financeiro/recebiveis-page').then((m) => ({ default: m.RecebiveisPage })),
)

// FINC-05 — Conciliação (exceções de pagamento + conferência do dia) e o caso.
// Lazy como toda aba: o `index` está no teto do orçamento de bundle.
const ConciliacaoPage = lazy(() =>
  import('@/features/financeiro/conciliacao/conciliacao-page').then((m) => ({ default: m.ConciliacaoPage })),
)
const CasoExcecaoPage = lazy(() =>
  import('@/features/financeiro/conciliacao/caso-page').then((m) => ({ default: m.CasoExcecaoPage })),
)
// Raiz de /financeiro: Boletos para quem lê o módulo; só a Conciliação para quem
// tem apenas `financeiro:conciliar` (o diretor financeiro).
const FinanceiroIndex = lazy(() => import('@/features/financeiro/financeiro-index'))
// AGR-07: boleto único (cobrança agrupada): lista e página de cada um. Aba de /financeiro, gate financeiro:ver.
const BoletosUnicosPage = lazy(() =>
  import('@/features/financeiro/boletos-unicos/boletos-unicos-page').then((m) => ({ default: m.BoletosUnicosPage })),
)
const BoletoUnicoPage = lazy(() =>
  import('@/features/financeiro/boletos-unicos/boleto-unico-page').then((m) => ({ default: m.BoletoUnicoPage })),
)

// Inadimplência — CONFIG GOVERNADA (PR-C3). Aba de /financeiro; cross-linkada
// do menu Configurações (Motor de regras).
const InadimplenciaConfigPage = lazy(() =>
  import('@/features/financeiro/inadimplencia-config-page').then((m) => ({ default: m.InadimplenciaConfigPage })),
)

const EstadoMigracaoPage = lazy(() =>
  import('@/features/estado-migracao/estado-migracao-page').then((m) => ({
    default: m.EstadoMigracaoPage,
  })),
)

const AnalyticsPage = lazy(() =>
  import('@/features/analytics/analytics-page').then((m) => ({ default: m.AnalyticsPage })),
)

const AdminPage = lazy(() =>
  import('@/features/admin/admin-page').then((m) => ({ default: m.AdminPage })),
)

const PerfilPage = lazy(() =>
  import('@/features/perfil/perfil-page').then((m) => ({ default: m.PerfilPage })),
)

// CRM / Funil de vendas — tela real (PR-CRM-2), consome /v1/crm.
const CRMPage = lazy(() =>
  import('@/features/crm/crm-page').then((m) => ({ default: m.CrmPage })),
)
// Clube de Benefícios (CLUBE-04) — casca da Fase 0, lazy como toda rota
// (D34: o backoffice do clube mora em `features/clube/`). A rota é gateada
// pelas permissões `clube_*` do menu; a flag mestre governa a NAVEGAÇÃO, e a
// tela é honesta em qualquer caso — não mostra dado que não existe.
const ClubeVisaoGeralPage = lazy(() =>
  import('@/features/clube/clube-visao-geral-page').then((m) => ({ default: m.ClubeVisaoGeralPage })),
)
// Backoffice do clube (CLUBE-07) — Catálogo (Serviços) e Planos, lazy como toda
// rota (D34). Gateadas por `clube_catalogo:ver`; a flag mestre governa a
// navegação (o item de menu) e o backend recusa (404) com ela desligada.
// COMPRAS (COMP-3) — lazy, como as demais frentes: com a flag desligada
// ninguém navega até aqui, e o bundle não paga por um módulo que não está no
// ar. `export default` nas duas páginas, então o `.then` não precisa remapear.
const ComprasCatalogoPage = lazy(() => import('@/features/compras/compras-catalogo-page'))
const ComprasSolicitacoesPage = lazy(() => import('@/features/compras/compras-solicitacoes-page'))
const ComprasItemNovoPage = lazy(() => import('@/features/compras/compras-item-novo-page'))
const OrdemCompraNovaPage = lazy(() => import('@/features/financeiro-corp/ordem-compra-nova-page'))
const ComprasItemPage = lazy(() => import('@/features/compras/compras-item-page'))
const ComprasConfiguracoesPage = lazy(() => import('@/features/compras/compras-configuracoes-page'))
const ComprasPendenciasPage = lazy(() => import('@/features/compras/compras-pendencias-page'))

const ClubeServicosPage = lazy(() =>
  import('@/features/clube/clube-servicos-page').then((m) => ({ default: m.ClubeServicosPage })),
)
const ClubePlanosPage = lazy(() =>
  import('@/features/clube/clube-planos-page').then((m) => ({ default: m.ClubePlanosPage })),
)
// Links de venda (CLUBE-16) — backoffice, gateado por `clube_config:ver`.
const ClubeLinksPage = lazy(() =>
  import('@/features/clube/clube-links-page').then((m) => ({ default: m.ClubeLinksPage })),
)
// Backoffice do clube (CLUBE-17) — Membros, Assinaturas, Faturas e Configurações,
// lazy como toda rota (D34). Gates próprios (clube_membro:ver / clube_financeiro:ver
// / clube_config:ver); a flag mestre governa a navegação e o backend recusa (404)
// com ela desligada.
const ClubeMembrosPage = lazy(() =>
  import('@/features/clube/clube-membros-page').then((m) => ({ default: m.ClubeMembrosPage })),
)
const ClubeAssinaturasPage = lazy(() =>
  import('@/features/clube/clube-assinaturas-page').then((m) => ({ default: m.ClubeAssinaturasPage })),
)
const ClubeFaturasPage = lazy(() =>
  import('@/features/clube/clube-faturas-page').then((m) => ({ default: m.ClubeFaturasPage })),
)
const ClubePendenciasPage = lazy(() =>
  import('@/features/clube/clube-pendencias-page').then((m) => ({ default: m.ClubePendenciasPage })),
)
const ClubeAssinaturasSemDividaPage = lazy(() =>
  import('@/features/clube/clube-assinaturas-sem-divida-page').then((m) => ({
    default: m.ClubeAssinaturasSemDividaPage,
  })),
)
const ClubeConfigPage = lazy(() =>
  import('@/features/clube/clube-config-page').then((m) => ({ default: m.ClubeConfigPage })),
)
// Backoffice de operação do clube (CLUBE-26) — Despacho, Agenda, Prestadores e
// Regiões, lazy como toda rota (D34). Gates: clube_chamado:ver (despacho) /
// clube_agenda:ver (demais); a flag mestre governa a navegação (404 do backend).
const ClubeDespachoPage = lazy(() =>
  import('@/features/clube/clube-despacho-page').then((m) => ({ default: m.ClubeDespachoPage })),
)
const ClubeAgendaPage = lazy(() =>
  import('@/features/clube/clube-agenda-page').then((m) => ({ default: m.ClubeAgendaPage })),
)
const ClubePrestadoresPage = lazy(() =>
  import('@/features/clube/clube-prestadores-page').then((m) => ({ default: m.ClubePrestadoresPage })),
)
const ClubeRegioesPage = lazy(() =>
  import('@/features/clube/clube-regioes-page').then((m) => ({ default: m.ClubeRegioesPage })),
)
// CLUBE-27 — Repasse a credenciados (fecha a Fase 2). Gate: clube_repasse:ver;
// fechar/marcar-pago são críticas (MFA). Flag mestre governa a navegação.
const ClubeRepassePage = lazy(() =>
  import('@/features/clube/clube-repasse-page').then((m) => ({ default: m.ClubeRepassePage })),
)
// CLUBE-32 — Parceiros, Ofertas e Cupons (fecha a Fase 3), lazy como toda rota
// (D34). Gate único clube_parceiro:ver; a flag mestre governa a navegação (404
// do backend com ela desligada).
const ClubeParceirosPage = lazy(() =>
  import('@/features/clube/clube-parceiros-page').then((m) => ({ default: m.ClubeParceirosPage })),
)
const ClubeOfertasPage = lazy(() =>
  import('@/features/clube/clube-ofertas-page').then((m) => ({ default: m.ClubeOfertasPage })),
)
const ClubeCuponsPage = lazy(() =>
  import('@/features/clube/clube-cupons-page').then((m) => ({ default: m.ClubeCuponsPage })),
)
// CLUBE-36 — Pontos (fecha a Fase 4): regras de ganho, recompensas, fila de
// resgates e ajuste manual em UMA rota com abas. Gate `clube_pontos:ver`; a flag
// mestre governa a navegação (404 do backend com a mestre desligada).
const ClubePontosPage = lazy(() =>
  import('@/features/clube/clube-pontos-page').then((m) => ({ default: m.ClubePontosPage })),
)
// CLUBE-39 — Indicadores do clube (só leitura). Gate `clube_indicador:ver`; a
// flag mestre governa a navegação (404 do backend com a mestre desligada). Lazy
// como toda rota do clube (D34) — fora do chunk `index`.
const ClubeIndicadoresPage = lazy(() =>
  import('@/features/clube/clube-indicadores-page').then((m) => ({ default: m.ClubeIndicadoresPage })),
)
const ComunicacaoPage = lazy(() =>
  import('@/features/comunicacao/comunicacao-page').then((m) => ({ default: m.ComunicacaoPage })),
)
const NotificacoesPage = lazy(() =>
  import('@/features/notificacoes/notificacoes-page').then((m) => ({ default: m.NotificacoesPage })),
)
const BuscaPage = lazy(() =>
  import('@/features/busca/busca-page').then((m) => ({ default: m.BuscaPage })),
)
const ContatosPage = lazy(() =>
  import('@/features/contatos/contatos-page').then((m) => ({ default: m.ContatosPage })),
)
const AtividadesPage = lazy(() =>
  import('@/features/atividades/atividades-page').then((m) => ({ default: m.AtividadesPage })),
)
const NovoLeadPage = lazy(() =>
  import('@/features/crm/novo-lead-page').then((m) => ({ default: m.NovoLeadPage })),
)
const LeadDetailPage = lazy(() =>
  import('@/features/crm/lead-detail-page').then((m) => ({ default: m.LeadDetailPage })),
)
const TagsCatalogoPage = lazy(() =>
  import('@/features/crm/tags-catalogo-page').then((m) => ({ default: m.TagsCatalogoPage })),
)
const AutomacoesPage = lazy(() =>
  import('@/features/crm/automacoes-page').then((m) => ({ default: m.AutomacoesPage })),
)
// Guia de fotos de vistoria (mig 000093, Fase 3) — lado CONSULTOR, dentro do CRM.
const VistoriaFotosPage = lazy(() =>
  import('@/features/crm/vistoria-fotos-page').then((m) => ({ default: m.VistoriaFotosPage })),
)

// Eventos / Ocorrências — tela real (W1-59)
const EventosPage = lazy(() =>
  import('@/features/eventos/eventos-page').then((m) => ({ default: m.EventosPage })),
)
const EventoDetailPage = lazy(() =>
  import('@/features/eventos/evento-detail-page').then((m) => ({ default: m.EventoDetailPage })),
)
const EventoCreatePage = lazy(() =>
  import('@/features/eventos/evento-create-page').then((m) => ({ default: m.EventoCreatePage })),
)
const ScorePage = lazy(() =>
  import('@/features/preview/preview-pages').then((m) => ({ default: m.ScorePage })),
)
// #199: a rota existia desde sempre apontando para a casca de prévia. A tela
// real assume o mesmo caminho — quem já tinha o link continua chegando no lugar
// certo, agora com conversa de verdade.
const AtendimentoPage = lazy(() =>
  import('@/features/atendimento/atendimento-page').then((m) => ({ default: m.AtendimentoPage })),
)
// Relatório de Frota — tela real (W1-115), consome /v1/veiculos/agregados-por-tipo,
// /v1/veiculos/series-entradas, /v1/boletos/mensalidade-por-tipo e /v1/eventos/agregados.
const RelatoriosPage = lazy(() =>
  import('@/features/relatorios/relatorios-page').then((m) => ({ default: m.RelatoriosPage })),
)
const EstacaoPage = lazy(() =>
  import('@/features/estacao/estacao-page').then((m) => ({ default: m.EstacaoPage })),
)
const ConfiguracoesPage = lazy(() =>
  import('@/features/config/config-page').then((m) => ({ default: m.ConfigPage })),
)
const RespostasProntasConfigPage = lazy(() =>
  import('@/features/atendimento/respostas-prontas-config-page').then((m) => ({
    default: m.RespostasProntasConfigPage,
  })),
)
const SetoresConfigPage = lazy(() =>
  import('@/features/estacao/setores-config-page').then((m) => ({ default: m.SetoresConfigPage })),
)
const ConfiguracaoCobrancaPage = lazy(() =>
  import('@/features/cobranca/cobranca-config-page').then((m) => ({
    default: m.ConfiguracaoCobrancaPage,
  })),
)
const CatalogoPage = lazy(() =>
  import('@/features/catalogo/catalogo-page').then((m) => ({ default: m.CatalogoPage })),
)
// Montagem do plano em página dedicada (#421) — o Sheet não comportava itens
// dinâmicos + seleção de produto por item.
const RegionaisPage = lazy(() =>
  import('@/features/tabela-preco/regionais/regionais-page').then((m) => ({ default: m.RegionaisPage })),
)
const CotacaoServicosPage = lazy(() =>
  import('@/features/tabela-preco/cotacao-servicos-page').then((m) => ({ default: m.CotacaoServicosPage })),
)
const CotacaoServicoEditorPage = lazy(() =>
  import('@/features/tabela-preco/servicos/servico-editor-page').then((m) => ({ default: m.CotacaoServicoEditorPage })),
)
const CotacaoPlanosPage = lazy(() =>
  import('@/features/tabela-preco/cotacao-planos-page').then((m) => ({ default: m.CotacaoPlanosPage })),
)
const CotacaoTabelasPage = lazy(() =>
  import('@/features/tabela-preco/cotacao-tabelas-page').then((m) => ({ default: m.CotacaoTabelasPage })),
)
const CotacaoTabelaPage = lazy(() =>
  import('@/features/tabela-preco/cotacao-tabelas-page').then((m) => ({ default: m.CotacaoTabelaPage })),
)
const CotacaoImplementosPage = lazy(() =>
  import('@/features/tabela-preco/implementos/cotacao-implementos-page').then((m) => ({ default: m.CotacaoImplementosPage })),
)
const CotacaoConfiguracoesPage = lazy(() =>
  import('@/features/tabela-preco/cotacao-configuracoes-page').then((m) => ({ default: m.CotacaoConfiguracoesPage })),
)
const RegrasPrecoPage = lazy(() =>
  import('@/features/catalogo/regras-preco-page').then((m) => ({ default: m.RegrasPrecoPage })),
)
const DescontosPontuaisPage = lazy(() =>
  import('@/features/crm/descontos-pontuais-page').then((m) => ({ default: m.DescontosPontuaisPage })),
)
const LimitesDescontoNovaPage = lazy(() =>
  import('@/features/catalogo/limites-desconto-nova-page').then((m) => ({ default: m.LimitesDescontoNovaPage })),
)
const FaixasAcrescimoNovaPage = lazy(() =>
  import('@/features/catalogo/faixas-acrescimo-nova-page').then((m) => ({ default: m.FaixasAcrescimoNovaPage })),
)
const FaixasAcrescimoHistoricoPage = lazy(() =>
  import('@/features/catalogo/faixas-acrescimo-historico-page').then((m) => ({ default: m.FaixasAcrescimoHistoricoPage })),
)
const CotacaoPlanoEditorPage = lazy(() =>
  import('@/features/tabela-preco/planos/plano-editor-page').then((m) => ({ default: m.CotacaoPlanoEditorPage })),
)
const DocumentosListaPage = lazy(() =>
  import('@/features/documentos/documentos-lista-page').then((m) => ({ default: m.DocumentosListaPage })),
)
const DocumentosModeloPage = lazy(() =>
  import('@/features/documentos/modelo-page').then((m) => ({ default: m.ModeloPage })),
)
const DocumentosMinhasAssinaturasPage = lazy(() =>
  import('@/features/documentos/minhas-assinaturas-page').then((m) => ({ default: m.MinhasAssinaturasPage })),
)
const DocumentosRepresentantePage = lazy(() =>
  import('@/features/documentos/representante-page').then((m) => ({ default: m.RepresentantePage })),
)
const DocumentoEmitidoPage = lazy(() =>
  import('@/features/documentos/documento-emitido-page').then((m) => ({ default: m.DocumentoEmitidoPage })),
)
const DocumentoPublicoPage = lazy(() =>
  import('@/features/documentos/documento-publico-page').then((m) => ({ default: m.DocumentoPublicoPage })),
)
const CaptacaoPage = lazy(() =>
  import('@/features/captacao/captacao-page').then((m) => ({ default: m.CaptacaoPage })),
)
const SimuladorCrmPage = lazy(() =>
  import('@/features/simulador/simulador-crm-page').then((m) => ({ default: m.SimuladorCrmPage })),
)
const CanalCredenciaisPage = lazy(() =>
  import('@/features/canal/canal-credenciais-page').then((m) => ({
    default: m.CanalCredenciaisPage,
  })),
)
const ConfigGetnetPage = lazy(() =>
  import('@/features/cobranca-cartao/config-getnet-page').then((m) => ({ default: m.ConfigGetnetPage })),
)
const CartaoPublicoPage = lazy(() =>
  import('@/features/cobranca-cartao/cartao-publico-page').then((m) => ({ default: m.CartaoPublicoPage })),
)
const CartaoRetornoPage = lazy(() =>
  import('@/features/cobranca-cartao/cartao-publico-page').then((m) => ({ default: m.CartaoRetornoPage })),
)
const IntegracoesBancariasPage = lazy(() =>
  import('@/features/integracoes-bancarias/integracoes-bancarias-page').then((m) => ({
    default: m.IntegracoesBancariasPage,
  })),
)

// ─── Skeleton de carregamento ─────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div
      className="flex min-h-screen items-center justify-center"
      role="status"
      aria-label="Carregando..."
    >
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      <span className="sr-only">Carregando página...</span>
    </div>
  )
}

// ─── Página de acesso negado ──────────────────────────────────────────────────

function ForbiddenPage() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">
        Sem permissão
      </h1>
      <p className="text-gray-500 dark:text-gray-400">
        Você não tem acesso a esta funcionalidade.
      </p>
    </main>
  )
}

// ─── Definição das rotas ──────────────────────────────────────────────────────

/**
 * RedirectPlano — os caminhos antigos do editor de plano precisam carregar
 * o `:id` para a rota nova; `<Navigate>` sozinho não interpola parâmetro.
 */
function RedirectPlano() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/crm/cotacao/planos/${id ?? 'novo'}`} replace />
}

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<PageSkeleton />}>{element}</Suspense>
}

function protegida(permissoes: readonly string[], element: React.ReactNode) {
  return <RequirePermission permissoes={permissoes}>{withSuspense(element)}</RequirePermission>
}

/**
 * A TABELA DE ROTAS, exportada para teste.
 *
 * ⚠️ `export` acrescentado na PR D1 porque a reorganização de Compras criou
 * quatro redirects e a decisão do dono pede "um teste por rota" — e até aqui
 * NENHUM teste importava este módulo (medido em 30/09/2026, registrado no
 * comentário de `permissoesDaRota`). Testar uma cópia da tabela escrita no
 * próprio teste não mede nada; o alvo tem de ser esta lista.
 *
 * Importar este módulo também AVALIA `permissoesDaRota` para cada rota, que
 * lança se alguma rota de menu ficar sem gate — então o arquivo de teste que o
 * importa é, de lambuja, a primeira rede a pegar isso antes do CI.
 */
const DestinoInicial = lazy(() => import('./destino-inicial'))

const routes: RouteObject[] = [
  // Redirect raiz
  {
    path: '/',
    element: withSuspense(<DestinoInicial />),
  },

  // Rotas públicas
  {
    path: '/login',
    element: withSuspense(<LoginPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // #827: fora do RequireAuth — o link de recuperação de senha chega sem sessão.
  {
    path: '/definir-senha',
    element: withSuspense(<DefinirSenhaPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Página pública de captação — fora do RequireAuth (o lead final não tem
  // sessão). Render + submit via endpoints públicos (unauth, skipAuth).
  {
    path: '/captacao/:slug',
    element: withSuspense(<CaptacaoPublicaPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Portal do associado — lista compartilhada com a entrada própria do
  // portal (portal.html, PORTAL-01). Sessão própria em cookie, sem AppShell.
  ...rotasDoPortal,
  // Guia de fotos de vistoria — token na URL, sem sessão (o associado não
  // tem login). Mesmo padrão de /captacao/:slug: fora do RequireAuth, sem
  // AppShell/nav (página solteira).
  {
    path: '/vistoria/:token',
    element: withSuspense(<VistoriaPublicaPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Aprovação do checklist do rastreador (RAST-11) — token na URL, sem sessão
  // (o associado chega pelo WhatsApp e não tem login). Fora do RequireAuth, sem
  // AppShell/nav. Não chama /v1/rastreador/capacidades nem rota autenticada.
  {
    path: '/aprovar/:token',
    element: withSuspense(<RastreadorAprovarPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Modo TV do rastreador (RAST-12, D15) — código curto na URL, sem sessão (a smart TV não
  // tem login). Fora do RequireAuth, sem AppShell/nav. Só chama /v1/rastreador-tv/{codigo}.
  {
    path: '/tv/:codigo',
    element: withSuspense(<RastreadorTvPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Assinatura eletrônica do Contrato de Adesão — token na URL, sem sessão (o
  // cliente final não tem login). Mesmo padrão de /captacao/:slug e
  // /vistoria/:token: fora do RequireAuth, sem AppShell/nav (página solteira).
  // Cadastro do cartão (CARTAO-03/04) — token na URL, sem sessão (o associado
  // não tem login). Fora do RequireAuth, sem AppShell/nav. `/cartao/retorno` é
  // para onde a Getnet devolve a pessoa (rota estática vence `:token`).
  {
    path: '/cartao/retorno',
    element: withSuspense(<CartaoRetornoPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/cartao/:token',
    element: withSuspense(<CartaoPublicoPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/assinar/:token',
    element: withSuspense(<AssinarPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Compartilhamento de proposta (paridade PowerCRM, 02/09/2026) — token na
  // URL, sem sessão (o lead não tem login). Mesmo padrão de /vistoria/:token
  // e /assinar/:token: fora do RequireAuth, sem AppShell/nav (página
  // solteira). API pública sob /v1/proposta-publica/{token} — path
  // DIFERENTE de propósito (mesma pegadinha de roteamento documentada no
  // backend), pra nginx não confundir a página com a API.
  {
    path: '/proposta/:token',
    element: withSuspense(<PropostaPublicaPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/simulacao/:token',
    element: withSuspense(<SimulacaoPublicaPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Link de assinatura de termo/contrato — token na URL, sem sessão. API em
  // /v1/documento-publico/{token}, path diferente da página de propósito.
  {
    path: '/documento/:token',
    element: withSuspense(<DocumentoPublicoPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/comparar/:token',
    element: withSuspense(<ComparacaoPublicaPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Área do MEMBRO EXTERNO (CLUBE-37) — realm próprio por cookie, fora do
  // RequireAuth e do AppShell. `/clube/minha-conta*` são ESTÁTICAS e, por serem
  // mais específicas, vencem o `/clube/:slug` do checkout no ranking do router
  // (mesmo racional do `/clube/retorno`). Entrar/primeiro acesso são abertas; a
  // conta fica sob o guardião de sessão do realm (UX; a autorização é do backend).
  {
    path: '/clube/minha-conta/entrar',
    element: withSuspense(<ClubeMembroEntrarPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/clube/minha-conta/primeiro-acesso',
    element: withSuspense(<ClubeMembroPrimeiroAcessoPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: withSuspense(<ClubeMembroGuard />),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/clube/minha-conta',
        element: withSuspense(<ClubeMinhaConta />),
      },
    ],
  },
  // Retorno do pagamento do checkout do clube (CLUBE-16) — ESTÁTICA, antes do
  // :slug para vencer no ranking do router. Página solteira, sem sessão.
  {
    path: '/clube/retorno',
    element: withSuspense(<ClubeRetornoPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Checkout público do link de venda do clube (CLUBE-16) — /clube/:slug, fora
  // do RequireAuth (visitante anônimo). O static /clube (Visão geral) e os
  // filhos /clube/servicos|planos|links são mais específicos e continuam
  // resolvendo para o backoffice autenticado; só um slug opaco cai aqui.
  {
    path: '/clube/:slug',
    element: withSuspense(<ClubeCheckoutPage />),
    errorElement: <RouteErrorBoundary />,
  },
  // Área do PRESTADOR (CLUBE-24) — realm próprio por cookie, fora do RequireAuth
  // e do AppShell. Entrar/primeiro acesso são abertas; a agenda e o detalhe ficam
  // sob o guardião de sessão do realm (UX; a autorização real é do backend).
  {
    path: '/prestador/entrar',
    element: withSuspense(<ClubePrestadorEntrarPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/prestador/primeiro-acesso',
    element: withSuspense(<ClubePrestadorPrimeiroAcessoPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: withSuspense(<ClubePrestadorGuard />),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/prestador',
        element: withSuspense(<ClubePrestadorAgendaPage />),
      },
      {
        path: '/prestador/chamados/:id',
        element: withSuspense(<ClubePrestadorChamadoPage />),
      },
    ],
  },
  // Portal do PARCEIRO (CLUBE-30) — realm próprio por cookie, fora do RequireAuth
  // e do AppShell. Entrar/primeiro acesso são abertas; validar/histórico/ofertas
  // ficam sob o guardião de sessão do realm (UX; a autorização real é do backend).
  {
    path: '/parceiro/entrar',
    element: withSuspense(<ClubeParceiroEntrarPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/parceiro/primeiro-acesso',
    element: withSuspense(<ClubeParceiroPrimeiroAcessoPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: withSuspense(<ClubeParceiroGuard />),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/parceiro',
        element: withSuspense(<ClubeParceiroValidarPage />),
      },
      {
        path: '/parceiro/historico',
        element: withSuspense(<ClubeParceiroHistoricoPage />),
      },
      {
        path: '/parceiro/ofertas',
        element: withSuspense(<ClubeParceiroOfertasPage />),
      },
    ],
  },

  // Rotas protegidas (requerem autenticação)
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        // #827: senha provisória vai para /definir-senha antes do MFA.
        element: <RequireSenhaDefinida />,
        children: [
          {
            // Gate de MFA obrigatório (feat: TOTP no login) — nenhuma página
            // abaixo é alcançável sem cadastro/step-up de MFA (dev-login isento).
            element: <RequireMfa />,
            children: [
              {
                // TenantProvider resolve a marca do tenant (GET /v1/tenant/branding)
                // e aplica o tema DENTRO da área autenticada (o endpoint é
                // ExigirAutenticado) — login/splash pré-auth usam o default Patrium.
                element: withSuspense(
                  <TenantProvider>
                    <AppShell />
                    <ScrollRestoration />
                  </TenantProvider>,
                ),
                children: [
                  {
                    path: '/dashboard',
                    element: protegida(permissoesDaRota('/dashboard'), <DashboardPage />),
                  },
                  {
                    // RAST-10: o quadro dos instaladores. O item de menu aponta para cá; a flag é lida pelo
                    // menu e o servidor revalida `rastreador:ver` em toda chamada.
                    path: '/rastreadores',
                    element: protegida(permissoesDaRota('/rastreadores'), <RastreadorKanbanPage />),
                  },
                  {
                    // RAST-8: a flag é lida pelo menu (sonda em /v1/rastreador/capacidades); a rota só
                    // exige `rastreador:ver`. As demais telas do módulo entram nas tarefas que as entregam.
                    // RAST-10: o item de menu passou a apontar para o quadro, então o gate do cadastro
                    // é declarado aqui (como o de /rastreadores/novo).
                    path: '/rastreadores/cadastro',
                    handle: { breadcrumb: 'Cadastro de rastreadores' },
                    element: protegida(['rastreador:ver'], <RastreadorCadastroPage />),
                  },
                  {
                    // RAST-9 (Tela 1): rota fora do menu (o item aponta para o cadastro), por isso o gate
                    // é declarado aqui. O servidor revalida `rastreador:criar` em toda chamada.
                    path: '/rastreadores/novo',
                    handle: { breadcrumb: 'Novo atendimento' },
                    element: protegida(['rastreador:criar'], <RastreadorNovoPage />),
                  },
                  {
                    // RAST-12: relatório (D19). O servidor revalida `rastreador:ver` em toda chamada.
                    path: '/rastreadores/relatorio',
                    handle: { breadcrumb: 'Relatório' },
                    element: protegida(['rastreador:ver'], <RastreadorRelatorioPage />),
                  },
                  {
                    // RAST-12: itens do checklist e código da TV. O servidor revalida `rastreador:gerenciar`.
                    path: '/rastreadores/configuracao',
                    handle: { breadcrumb: 'Configuração' },
                    element: protegida(['rastreador:gerenciar'], <RastreadorConfiguracaoPage />),
                  },
                  {
                    path: '/estado-migracao',
                    element: withSuspense(<EstadoMigracaoPage />),
                  },
                  {
                    path: '/config/uso-do-produto',
                    handle: { breadcrumb: 'Uso do produto' },
                    element: protegida(['analytics:ver'], <AnalyticsPage />),
                  },
                  {
                    path: '/crm',
                    element: withSuspense(<CRMPage />),
                  },
                  {
                    path: '/clube',
                    element: protegida(permissoesDaRota('/clube'), <ClubeVisaoGeralPage />),
                  },
                  {
                    path: '/clube/servicos',
                    handle: { breadcrumb: 'Serviços' },
                    element: protegida(permissoesDaRota('/clube/servicos'), <ClubeServicosPage />),
                  },
                  {
                    path: '/clube/planos',
                    handle: { breadcrumb: 'Planos' },
                    element: protegida(permissoesDaRota('/clube/planos'), <ClubePlanosPage />),
                  },
                  {
                    path: '/clube/links',
                    handle: { breadcrumb: 'Links de venda' },
                    element: protegida(permissoesDaRota('/clube/links'), <ClubeLinksPage />),
                  },
                  {
                    path: '/clube/membros',
                    handle: { breadcrumb: 'Membros' },
                    element: protegida(permissoesDaRota('/clube/membros'), <ClubeMembrosPage />),
                  },
                  {
                    path: '/clube/assinaturas',
                    handle: { breadcrumb: 'Assinaturas' },
                    element: protegida(permissoesDaRota('/clube/assinaturas'), <ClubeAssinaturasPage />),
                  },
                  {
                    path: '/clube/faturas',
                    handle: { breadcrumb: 'Faturas' },
                    element: protegida(permissoesDaRota('/clube/faturas'), <ClubeFaturasPage />),
                  },
                  {
                    path: '/clube/pendencias-financeiras',
                    handle: { breadcrumb: 'Pendências financeiras' },
                    element: protegida(
                      permissoesDaRota('/clube/pendencias-financeiras'),
                      <ClubePendenciasPage />,
                    ),
                  },
                  {
                    path: '/clube/assinaturas-sem-divida',
                    handle: { breadcrumb: 'Assinaturas sem dívida em aberto' },
                    element: protegida(
                      permissoesDaRota('/clube/assinaturas-sem-divida'),
                      <ClubeAssinaturasSemDividaPage />,
                    ),
                  },
                  {
                    path: '/clube/config',
                    handle: { breadcrumb: 'Configurações do clube' },
                    element: protegida(permissoesDaRota('/clube/config'), <ClubeConfigPage />),
                  },
                  {
                    path: '/clube/despacho',
                    handle: { breadcrumb: 'Despacho' },
                    element: protegida(permissoesDaRota('/clube/despacho'), <ClubeDespachoPage />),
                  },
                  {
                    path: '/clube/agenda',
                    handle: { breadcrumb: 'Agenda' },
                    element: protegida(permissoesDaRota('/clube/agenda'), <ClubeAgendaPage />),
                  },
                  {
                    path: '/clube/prestadores',
                    handle: { breadcrumb: 'Prestadores' },
                    element: protegida(permissoesDaRota('/clube/prestadores'), <ClubePrestadoresPage />),
                  },
                  {
                    path: '/clube/regioes',
                    handle: { breadcrumb: 'Regiões' },
                    element: protegida(permissoesDaRota('/clube/regioes'), <ClubeRegioesPage />),
                  },
                  {
                    path: '/clube/repasses',
                    handle: { breadcrumb: 'Repasse a credenciados' },
                    element: protegida(permissoesDaRota('/clube/repasses'), <ClubeRepassePage />),
                  },
                  {
                    path: '/clube/parceiros',
                    handle: { breadcrumb: 'Parceiros' },
                    element: protegida(permissoesDaRota('/clube/parceiros'), <ClubeParceirosPage />),
                  },
                  {
                    path: '/clube/ofertas',
                    handle: { breadcrumb: 'Ofertas' },
                    element: protegida(permissoesDaRota('/clube/ofertas'), <ClubeOfertasPage />),
                  },
                  {
                    path: '/clube/cupons',
                    handle: { breadcrumb: 'Cupons' },
                    element: protegida(permissoesDaRota('/clube/cupons'), <ClubeCuponsPage />),
                  },
                  {
                    path: '/clube/pontos',
                    handle: { breadcrumb: 'Pontos e recompensas' },
                    element: protegida(permissoesDaRota('/clube/pontos'), <ClubePontosPage />),
                  },
                  {
                    path: '/clube/indicadores',
                    handle: { breadcrumb: 'Indicadores' },
                    element: protegida(permissoesDaRota('/clube/indicadores'), <ClubeIndicadoresPage />),
                  },
                  {
                    path: '/notificacoes',
                    handle: { breadcrumb: 'Notificações' },
                    element: withSuspense(<NotificacoesPage />),
                  },
                  {
                    path: '/comunicacao',
                    handle: { breadcrumb: 'Comunicação' },
                    element: protegida(permissoesDaRota('/comunicacao'), <ComunicacaoPage />),
                  },
                  {
                    path: '/busca',
                    handle: { breadcrumb: 'Busca' },
                    element: withSuspense(<BuscaPage />),
                  },
                  {
                    path: '/contatos',
                    element: withSuspense(<ContatosPage />),
                  },
                  {
                    path: '/atividades',
                    element: withSuspense(<AtividadesPage />),
                  },
                  {
                    path: '/crm/leads/novo',
                    handle: { breadcrumb: 'Nova negociação' },
                    element: withSuspense(<NovoLeadPage />),
                  },
                  {
                    path: '/crm/leads/:id',
                    handle: { breadcrumb: 'Ficha da negociação' },
                    element: withSuspense(<LeadDetailPage />),
                  },
                  {
                    path: '/crm/tags',
                    element: withSuspense(<TagsCatalogoPage />),
                  },
                  {
                    path: '/crm/automacoes',
                    handle: { breadcrumb: 'Automações' },
                    element: withSuspense(<AutomacoesPage />),
                  },
                  {
                    path: '/crm/leads/:id/bens/:bemId/vistoria/fotos',
                    handle: { breadcrumb: 'Guia de fotos da vistoria' },
                    element: withSuspense(<VistoriaFotosPage />),
                  },
                  {
                    path: '/associados',
                    element: protegida(permissoesDaRota('/associados'), <AssociadosPage />),
                  },
                  {
                    path: '/associados/novo',
                    handle: { breadcrumb: 'Novo associado' },
                    element: protegida(['associado:criar'], <AssociadoCreatePage />),
                  },
                  {
                    // Visão 360º: layout com header/badges + abas, todas sob
                    // /associados/:id/* — ver associado-detail-layout.tsx.
                    path: '/associados/:id',
                    handle: { breadcrumb: 'Ficha do associado' },
                    element: protegida(['associado:ver'], <AssociadoDetailLayout />),
                    children: [
                      { index: true, element: withSuspense(<AssociadoDadosTab />) },
                      { path: 'bens', element: withSuspense(<AssociadoBensTab />) },
                      { path: 'financeiro', element: withSuspense(<AssociadoFinanceiroTab />) },
                      { path: 'eventos', element: withSuspense(<AssociadoEventosTab />) },
                      { path: 'beneficiarios', element: withSuspense(<AssociadoBeneficiariosTab />) },
                      { path: 'background', element: withSuspense(<AssociadoBackgroundTab />) },
                    ],
                  },
                  {
                    path: '/beneficiarios',
                    element: protegida(permissoesDaRota('/beneficiarios'), <BeneficiariosPage />),
                  },
                  {
                    path: '/beneficiarios/novo',
                    handle: { breadcrumb: 'Novo beneficiário' },
                    element: protegida(['beneficiario:criar'], <BeneficiarioCreatePage />),
                  },
                  {
                    path: '/beneficiarios/:id',
                    handle: { breadcrumb: 'Ficha do beneficiário' },
                    element: protegida(['beneficiario:ver'], <BeneficiarioDetailPage />),
                  },
                  {
                    path: '/bens',
                    element: protegida(permissoesDaRota('/bens'), <BensPage />),
                  },
                  {
                    path: '/bens/novo',
                    handle: { breadcrumb: 'Novo bem' },
                    element: protegida(['bem:criar'], <BemCreatePage />),
                  },
                  {
                    path: '/bens/:id',
                    handle: { breadcrumb: 'Ficha do bem' },
                    element: protegida(['bem:ver'], <BemDetailPage />),
                  },
                  {
                    // Financeiro = módulo único com navegação interna por abas
                    // (Boletos/Plano de contas/Centros de custo/Razão), todas
                    // sob /financeiro/* — ver financeiro-layout.tsx.
                    path: '/financeiro',
                    element: protegida(permissoesDaRota('/financeiro'), <FinanceiroLayout />),
                    children: [
                      // A raiz abre para quem entra no módulo; o FinanceiroIndex decide entre Boletos e Conciliação.
                      { index: true, element: protegida(permissoesDaRota('/financeiro'), <FinanceiroIndex />) },
                      { path: 'conciliacao', element: protegida(permissoesDaAba('/financeiro/conciliacao'), <ConciliacaoPage />) },
                      { path: 'boletos-unicos', element: protegida([permissaoDaAba('/financeiro/boletos-unicos')], <BoletosUnicosPage />) },
                      {
                        path: 'boletos-unicos/:id',
                        handle: { breadcrumb: 'Boleto único' },
                        element: protegida([permissaoDaAba('/financeiro/boletos-unicos')], <BoletoUnicoPage />),
                      },
                      {
                        path: 'conciliacao/casos/:id',
                        handle: { breadcrumb: 'Caso de pagamento' },
                        element: protegida(permissoesDaAba('/financeiro/conciliacao'), <CasoExcecaoPage />),
                      },
                      { path: 'plano-contas', element: protegida([permissaoDaAba('/financeiro/plano-contas')], <PlanoContasPage />) },
                      { path: 'centros-custo', element: protegida([permissaoDaAba('/financeiro/centros-custo')], <CentrosCustoPage />) },
                      { path: 'razao', element: protegida([permissaoDaAba('/financeiro/razao')], <RazaoPage />) },
                      // F3 — Fornecedores mudou para `/compras/fornecedores`. A
                      // rota antiga FICA, redirecionando: favorito e link
                      // salvo existem, e quebrá-los é custo sem ganho (mesma
                      // política da `/financeiro/ordens-compra`).
                      //
                      // ⚠️ `RedirectComQuery`, não `<Navigate>`: o
                      // `<Navigate>` descarta a query, e com ela um `?id=` de
                      // favorito — achado da revisão da PR D1, fixado em
                      // `compras-redirects.test.tsx`.
                      //
                      // SEM `permissaoDaAba` aqui: a aba deixou de existir
                      // nesta barra e aquela função LANÇA para aba
                      // desconhecida. O gate de leitura é cobrado no destino.
                      { path: 'fornecedores', element: <RedirectComQuery para="/compras/fornecedores" /> },
                      // U3: a OC mudou para /compras/ordens-compra. A rota antiga
                      // FICA, redirecionando, por pelo menos um ciclo — favorito e
                      // link salvo existem, e quebrá-los é custo sem ganho.
                      //
                      // O redirect herda o gate do PAI `/financeiro`
                      // (financeiro:ver OU receber:ver OU comissao:ver): quem
                      // não passa nele vê Forbidden em vez de ser redirecionado.
                      // É o correto — só tem link antigo salvo quem já abria a
                      // aba, e para abri-la precisava justamente desse gate. O
                      // `aprovador_compras`, que nunca alcançou a aba, chega
                      // pela rota nova, que não depende do Financeiro.
                      { path: 'ordens-compra', element: <RedirectComQuery para="/compras" /> },
                      { path: 'titulos-pagar', element: protegida([permissaoDaAba('/financeiro/titulos-pagar')], <TitulosPagarPage />) },
                      { path: 'contratos-recorrentes', element: protegida([permissaoDaAba('/financeiro/contratos-recorrentes')], <ContratosRecorrentesPage />) },
                      { path: 'comissoes', element: protegida([permissaoDaAba('/financeiro/comissoes')], <ComissoesParceirosPage />) },
                      { path: 'contas-receber', element: protegida([permissaoDaAba('/financeiro/contas-receber')], <RecebiveisPage />) },
                      { path: 'inadimplencia', element: protegida([permissaoDaAba('/financeiro/inadimplencia')], <InadimplenciaConfigPage />) },
                    ],
                  },
                  {
                    // Rede comercial (REDE-09): casca com abas; cada aba declara o
                    // próprio gate. A flag `rede.habilitada` é lida pela casca
                    // (sonda em /v1/rede/capacidades), não pelo guard de rota.
                    path: '/rede',
                    element: protegida(permissoesDaRota('/rede'), <RedeLayout />),
                    children: [
                      { index: true, element: protegida([permissaoDaAbaRede('/rede')], <MinhaRedePage />) },
                      {
                        path: 'consultores',
                        handle: { breadcrumb: 'Consultores' },
                        element: protegida([permissaoDaAbaRede('/rede/consultores')], <ConsultoresPage />),
                      },
                      {
                        path: 'cooperativas',
                        handle: { breadcrumb: 'Cooperativas' },
                        element: protegida([permissaoDaAbaRede('/rede/cooperativas')], <CooperativasRedePage />),
                      },
                      {
                        path: 'metas',
                        handle: { breadcrumb: 'Metas e comissão' },
                        element: protegida([permissaoDaAbaRede('/rede/metas')], <MetasComissaoPage />),
                      },
                      {
                        path: 'indicadores',
                        handle: { breadcrumb: 'Indicadores' },
                        element: protegida([permissaoDaAbaRede('/rede/indicadores')], <IndicadoresRedePage />),
                      },
                      {
                        path: 'fechamento',
                        handle: { breadcrumb: 'Fechamento' },
                        element: protegida([permissaoDaAbaRede('/rede/fechamento')], <FechamentoRedePage />),
                      },
                      {
                        path: 'extratos',
                        handle: { breadcrumb: 'Extratos' },
                        element: protegida([permissaoDaAbaRede('/rede/extratos')], <ExtratosRedePage />),
                      },
                      {
                        path: 'permissoes',
                        handle: { breadcrumb: 'Permissões' },
                        element: protegida([permissaoDaAbaRede('/rede/permissoes')], <PermissoesRedePage />),
                      },
                    ],
                  },
                  {
                    // PR D1 — `/compras` É A LISTA DE ORDENS DE COMPRA, a tela
                    // que o comprador abre todo dia (decisão do dono,
                    // 08/10/2026). Era o catálogo, que mudou para
                    // `/compras/itens`.
                    //
                    // O gate vem do item de menu (`financeiro:ver` OU
                    // `compras:ver`) e NÃO depende da flag do catálogo — é a
                    // mesma garantia da rota antiga da OC, agora na raiz do
                    // módulo: desligar `compras.catalogo.habilitado` nunca tira
                    // a OC de quem a usa.
                    path: '/compras',
                    element: protegida(permissoesDaRota('/compras'), <OrdensCompraPage />),
                  },
                  {
                    // F3 — a LISTA de fornecedores, no grupo Compras. É item
                    // de menu, então o gate vem de `permissoesDaRota`
                    // (`financeiro:ver`, o mesmo que o backend exige nas 7
                    // rotas de fornecedor).
                    path: '/compras/fornecedores',
                    handle: { breadcrumb: 'Fornecedores' },
                    element: protegida(permissoesDaRota('/compras/fornecedores'), <FornecedoresPage />),
                  },
                  {
                    // Cadastro em página. NÃO entra no menu (chega-se por
                    // "Novo fornecedor"), e por isso o gate é escrito AQUI em
                    // vez de vir de `permissoesDaRota`, que lê o item de menu.
                    //
                    // `financeiro:configurar` é o MESMO que o backend exige em
                    // `POST /v1/financeiro/fornecedores`: abrir a tela para
                    // quem não configura daria 403 depois de dez campos
                    // preenchidos.
                    //
                    // A rota estática aparece antes da `:id` só por leitura —
                    // o React Router v6 escolhe por ESPECIFICIDADE, não por
                    // posição, então quem garante que `/novo` não vira um id é
                    // o ranking, e não esta ordem (a mesma nota está na rota
                    // de `/compras/itens/novo`).
                    path: '/compras/fornecedores/novo',
                    handle: { breadcrumb: 'Novo fornecedor' },
                    element: protegida(['financeiro:configurar'], <FornecedorNovoPage />),
                  },
                  {
                    // A FICHA. Também fora do menu (chega-se pela linha da
                    // lista), gate de LEITURA `financeiro:ver`: a ficha não
                    // mostra nada que a lista já não mostre — documento e
                    // banco saem mascarados, e ver em claro é ação própria com
                    // `financeiro:ver_pii` + MFA, cobrada pelo BACKEND.
                    path: '/compras/fornecedores/:id',
                    handle: { breadcrumb: 'Fornecedor' },
                    element: protegida(['financeiro:ver'], <FornecedorPage />),
                  },
                  {
                    // O catálogo de itens, no endereço novo. A tela de duas
                    // abas (cadastrados + fila de cadastro) é a PR D2; aqui
                    // serve o hub que já existe, que continua levando a "Novo
                    // item", às famílias e às solicitações.
                    path: '/compras/itens',
                    handle: { breadcrumb: 'Itens' },
                    element: protegida(permissoesDaRota('/compras/itens'), <ComprasCatalogoPage />),
                  },
                  {
                    // A fila de solicitações continua em rota própria nesta PR;
                    // a D2 a transforma na aba "Na fila de cadastro" de
                    // `/compras/itens` e põe o redirect aqui. Sai do MENU já
                    // agora (o menu tem quatro itens), mas segue alcançável
                    // pelo hub — item de menu que a D2 vai mudar de lugar duas
                    // vezes é churn de navegação para quem usa.
                    path: '/compras/solicitacoes',
                    handle: { breadcrumb: 'Solicitações de item' },
                    element: protegida(['compras:ver'], <ComprasSolicitacoesPage />),
                  },
                  {
                    // Configurações de Compras: as abas Tolerância de valor e
                    // Famílias de itens. O gate vem do item de menu, que aqui é
                    // a UNIÃO (`requiresAnyGate`): `financeiro:ver` OU
                    // `compras:ver`. Quais abas cada um vê é decisão da TELA —
                    // a rota não pode barrar quem o menu mostra.
                    path: '/compras/configuracoes',
                    handle: { breadcrumb: 'Configurações de Compras' },
                    element: protegida(permissoesDaRota('/compras/configuracoes'), <ComprasConfiguracoesPage />),
                  },
                  {
                    // FATIA B — cadastro de item em tela própria. Como a ficha,
                    // NÃO entra no menu (chega-se por "Novo item" no hub), e por
                    // isso o gate é escrito aqui em vez de vir de
                    // `permissoesDaRota`, que lê o item de menu.
                    //
                    // `compras:gerenciar` é o mesmo que o BACKEND exige em
                    // `POST /v1/compras/itens`: abrir a tela para quem não
                    // gerencia daria um 403 depois de a pessoa preencher dez
                    // campos.
                    //
                    // A rota ESTÁTICA aparece antes da `:id` só por leitura: o
                    // React Router v6 escolhe por ESPECIFICIDADE, não por
                    // posição, então quem garante que `/novo` não vira um id é
                    // o ranking, e não esta ordem. Trocá-las de lugar não muda
                    // nada — dizer o contrário seria prometer uma defesa que o
                    // código não tem.
                    path: '/compras/itens/novo',
                    handle: { breadcrumb: 'Novo item' },
                    element: protegida(['compras:gerenciar'], <ComprasItemNovoPage />),
                  },
                  {
                    // U2b — ficha do item, as quatro dimensões. NÃO entra no
                    // menu: é tela de detalhe, alcançada pela linha do
                    // catálogo, como a ficha do bem e a do lead. Por isso o
                    // gate é escrito aqui em vez de vir de `permissoesDaRota`,
                    // que lê o item de menu e lançaria "rota fora do menu".
                    //
                    // `compras:ver` é o mesmo gate do catálogo: a ficha não
                    // mostra nada que a lista já não mostre — quem escreve
                    // precisa de `compras:gerenciar`, e isso o BACKEND exige
                    // em cada rota de escrita, não a tela.
                    path: '/compras/itens/:id',
                    handle: { breadcrumb: 'Item do catálogo' },
                    element: protegida(['compras:ver'], <ComprasItemPage />),
                  },
                  {
                    // COMP-7 virou ABA de Configurações (PR D1). A rota antiga
                    // FICA, redirecionando: favorito e link salvo existem, e
                    // quebrá-los é custo sem ganho — mesma política da
                    // `/financeiro/ordens-compra`.
                    //
                    // SEM `protegida`: o destino cobra o gate. Barrar aqui
                    // ANTES de redirecionar mostraria "sem permissão" a quem
                    // tem acesso à outra aba, e o redirect não revela nada —
                    // ele não lê dado nenhum.
                    path: '/compras/tolerancias',
                    element: <RedirectComQuery para="/compras/configuracoes?aba=tolerancia" />,
                  },
                  {
                    // #1108 (PR A) — a fila de notas ambíguas. Gate vindo do
                    // MENU (`permissoesDaRota`), como as outras rotas de
                    // /compras: a leitura pede `financeiro:ver`; DECIDIR pede
                    // `financeiro:excecao`, que o BACKEND cobra em cada rota de
                    // escrita — a tela esconde os controles, o servidor recusa.
                    //
                    // Sem flag de propósito: a flag da conciliação governa quem
                    // ENFILEIRA, não quem lê. Ver o comentário do item de menu.
                    path: '/compras/pendencias',
                    element: protegida(permissoesDaRota('/compras/pendencias'), <ComprasPendenciasPage />),
                  },
                  {
                    // Famílias de itens virou ABA de Configurações (PR D1); a
                    // rota antiga redireciona. Ver a nota da `/compras/tolerancias`
                    // sobre por que o redirect não leva `protegida`.
                    path: '/compras/familias',
                    element: <RedirectComQuery para="/compras/configuracoes?aba=familias" />,
                  },
                  {
                    // FATIA C — a OC deixou de ser modal. Como as outras telas
                    // de criação desta frente, NÃO entra no menu (chega-se por
                    // "Nova ordem de compra" na lista), então o gate é escrito
                    // aqui em vez de vir de `permissoesDaRota`.
                    //
                    // `financeiro:configurar` é o mesmo que o BACKEND exige em
                    // `POST /v1/financeiro/ordens-compra`: abrir a tela para
                    // quem não pode criar daria 403 depois de a pessoa
                    // preencher seis campos por linha.
                    //
                    // A rota ESTÁTICA `/nova` convive com a de listagem sem
                    // ambiguidade: são dois caminhos distintos, não um `:id`.
                    path: '/compras/ordens-compra/nova',
                    handle: { breadcrumb: 'Nova ordem de compra' },
                    element: protegida(['financeiro:configurar'], <OrdemCompraNovaPage />),
                  },
                  {
                    // PR D1 — a lista subiu para `/compras`. A rota da U3 fica,
                    // redirecionando: ela nasceu em setembro e já tem link
                    // salvo (e é o destino do redirect ANTIGO, o de
                    // `/financeiro/ordens-compra`, que agora aponta direto para
                    // `/compras` — redirect em cadeia funciona, mas faz duas
                    // navegações onde uma basta).
                    path: '/compras/ordens-compra',
                    element: <RedirectComQuery para="/compras" />,
                  },
                  {
                    path: '/rateio',
                    element: protegida(permissoesDaRota('/rateio'), <RateioPage />),
                  },
                  {
                    path: '/rateio/abrir-fechamento',
                    handle: { breadcrumb: 'Abrir/recalcular fechamento' },
                    element: protegida(['rateio:fechar'], <AbrirFechamentoPage />),
                  },
                  {
                    path: '/eventos',
                    element: protegida(permissoesDaRota('/eventos'), <EventosPage />),
                  },
                  {
                    path: '/eventos/novo',
                    handle: { breadcrumb: 'Registrar evento' },
                    element: protegida(['evento:criar'], <EventoCreatePage />),
                  },
                  {
                    path: '/eventos/:id',
                    handle: { breadcrumb: 'Ficha da ocorrência' },
                    element: protegida(['evento:ver'], <EventoDetailPage />),
                  },
                  {
                    path: '/score',
                    element: withSuspense(<ScorePage />),
                  },
                  {
                    path: '/atendimento',
                    element: withSuspense(<AtendimentoPage />),
                  },
                  {
                    // Estação e Atendimento são rotas próprias: a conversa entre
                    // colegas e a conversa com o associado são produtos distintos,
                    // com trilhos distintos — só o formato de tela é o mesmo.
                    path: '/estacao',
                    element: withSuspense(<EstacaoPage />),
                  },
                  {
                    path: '/relatorios',
                    element: protegida(permissoesDaRota('/relatorios'), <RelatoriosPage />),
                  },
                  {
                    // ALTA 1 da revisão independente da D1.2: esta rota estava
                    // SEM gate, e isso tornava o resto do PR contornável
                    // tirando o sufixo da URL. `ConfigPage` cai em
                    // `modulosVisiveis[0]` quando o pathname é só `/config`, e
                    // `modulosVisiveis` era filtrado por gate de SEÇÃO — as
                    // seções de Gerais e Integrações não têm gate próprio,
                    // então `/config` servia exatamente as telas que gatear
                    // `/config/gerais` deveria ter fechado.
                    //
                    // Medido pelo revisor com perfil `voluntario` (nenhuma das
                    // 11 permissões): módulos visíveis em `/config` eram
                    // ["Gerais","Integrações"], com 7 seções dentro.
                    path: '/config',
                    element: protegida(permissoesDaRota('/config'), <ConfiguracoesPage />),
                  },
                  // #897 fatia A: um path por módulo, porque o estado ativo da
                  // sidebar é decidido só pelo pathname. São rotas estáticas, e
                  // não `/config/:modulo`, para não competir com as seções que
                  // já moram sob /config (cobranca, integracoes-bancarias…).
                  //
                  // A lista vem dos filhos do menu, e não de MODULOS_CONFIG:
                  // este arquivo está no chunk inicial, e importar aquela lista
                  // levaria junto o texto de todas as seções.
                  //
                  // D1.2 — TODO módulo passa por `protegida`, sem exceção.
                  //
                  // O ramo `: withSuspense(...)` que existia aqui era CÓDIGO
                  // MORTO a partir do momento em que `Gerais` e `Integrações`
                  // ganharam gate explícito — e antes disso era pior que
                  // morto: era a linha que transformava "gate vazio" em
                  // "nenhum gate", deixando as duas telas abertas a qualquer
                  // sessão autenticada. O comentário anterior descrevia
                  // corretamente que `[]` recusa todo mundo e, em seguida,
                  // fazia o oposto para evitar a recusa.
                  //
                  // `permissoesDaRota` agora LANÇA em lista vazia.
                  //
                  // QUEM PEGA O QUÊ, medido acrescentando um filho novo de
                  // /config sem gate — são portões DIFERENTES para casos
                  // diferentes, e eu já errei descrevendo isto duas vezes, nas
                  // duas direções:
                  //
                  //   módulo novo de /config sem gate
                  //     guard de CI .... exit 0, CEGO. Ele lê o TEXTO daqui,
                  //                      onde `protegida(permissoesDaRota(...))`
                  //                      é incondicional e correto; não tem
                  //                      como saber que a LISTA virá vazia.
                  //     suíte .......... 4 vermelhos em 3 arquivos:
                  //                      contrato-do-gate.test.tsx ×2,
                  //                      modulos.test.ts, nav-items.test.ts.
                  //
                  //   rota nova escrita aqui sem gate  ← o caso IRMÃO
                  //     guard de CI .... reprova, nomeia a rota e ensina.
                  //     suíte .......... nada: zero arquivos de teste
                  //                      importam `@/routes`.
                  //
                  // E o que NENHUM dos dois cobre: o `throw` roda na avaliação
                  // do módulo, antes do React montar, então nenhum error
                  // boundary o pega — em produção seria SPA em branco. É por
                  // isso que os dois portões importam, e por que nenhum deles
                  // sozinho é a resposta.
                  ...modulosDeConfig.map((modulo) => ({
                    path: modulo.to,
                    element: protegida(permissoesDaRota(modulo.to), <ConfiguracoesPage />),
                  })),
                  // Catálogo, captação e regras de preço aparecem em Configurações ›
                  // CRM, mas as rotas seguem sob /crm; as antigas de /config viram
                  // redirect para não quebrar link salvo.
                  {
                    path: '/crm/catalogo',
                    element: protegida(['catalogo:ver'], <CatalogoPage />),
                  },
                  { path: '/crm/catalogo/planos/novo', element: <Navigate to="/crm/cotacao/planos/novo" replace /> },
                  { path: '/crm/catalogo/planos/:id', element: protegida(['catalogo:ver'], <RedirectPlano />) },
                  { path: '/crm/cotacao', element: <Navigate to="/crm/cotacao/configuracoes" replace /> },
                  {
                    path: '/crm/cotacao/configuracoes',
                    handle: { breadcrumb: 'Cotação e preço' },
                    element: protegida(['tabela_preco:ver'], <CotacaoConfiguracoesPage />),
                  },
                  {
                    path: '/crm/cotacao/regionais',
                    handle: { breadcrumb: 'Cotação e preço' },
                    element: protegida(['tabela_preco:ver'], <RegionaisPage />),
                  },
                  {
                    path: '/crm/cotacao/regionais/nova',
                    handle: { breadcrumb: 'Nova regional' },
                    element: protegida(['tabela_preco:gerenciar'], <RegionaisPage />),
                  },
                  {
                    path: '/crm/cotacao/regionais/:id',
                    handle: { breadcrumb: 'Regional' },
                    element: protegida(['tabela_preco:ver'], <RegionaisPage />),
                  },
                  {
                    path: '/crm/cotacao/servicos',
                    handle: { breadcrumb: 'Cotação e preço' },
                    element: protegida(['catalogo:ver'], <CotacaoServicosPage />),
                  },
                  {
                    path: '/crm/cotacao/servicos/:lado/:id',
                    handle: { breadcrumb: 'Serviço' },
                    element: protegida(['catalogo:gerenciar'], <CotacaoServicoEditorPage />),
                  },
                  {
                    path: '/crm/cotacao/planos',
                    handle: { breadcrumb: 'Cotação e preço' },
                    element: protegida(['catalogo:ver'], <CotacaoPlanosPage />),
                  },
                  {
                    path: '/crm/cotacao/planos/novo',
                    handle: { breadcrumb: 'Novo plano' },
                    element: protegida(['catalogo:gerenciar'], <CotacaoPlanoEditorPage />),
                  },
                  {
                    path: '/crm/cotacao/planos/:id',
                    handle: { breadcrumb: 'Plano' },
                    element: protegida(['catalogo:ver'], <CotacaoPlanoEditorPage />),
                  },
                  {
                    path: '/crm/cotacao/tabelas',
                    handle: { breadcrumb: 'Cotação e preço' },
                    element: protegida(['tabela_preco:ver'], <CotacaoTabelasPage />),
                  },
                  {
                    path: '/crm/cotacao/tabelas/:id',
                    handle: { breadcrumb: 'Tabela de preço' },
                    element: protegida(['tabela_preco:ver'], <CotacaoTabelaPage />),
                  },
                  {
                    path: '/crm/cotacao/implementos',
                    handle: { breadcrumb: 'Cotação e preço' },
                    element: protegida(['catalogo:ver'], <CotacaoImplementosPage />),
                  },
                  {
                    path: '/crm/regras-preco',
                    element: protegida(['desconto_acrescimo:ver'], <RegrasPrecoPage />),
                  },
                  {
                    path: '/crm/descontos-pontuais',
                    handle: { breadcrumb: 'Descontos pontuais' },
                    element: protegida(['desconto:liberar_pontual'], <DescontosPontuaisPage />),
                  },
                  { path: '/crm/limites-desconto', element: <Navigate to="/crm/regras-preco?aba=limites" replace /> },
                  {
                    path: '/crm/limites-desconto/nova',
                    handle: { breadcrumb: 'Nova regra' },
                    element: protegida(['desconto_acrescimo:configurar'], <LimitesDescontoNovaPage />),
                  },
                  { path: '/crm/faixas-acrescimo', element: <Navigate to="/crm/regras-preco?aba=criterio" replace /> },
                  {
                    path: '/crm/faixas-acrescimo/nova',
                    handle: { breadcrumb: 'Nova faixa' },
                    element: protegida(['desconto_acrescimo:configurar'], <FaixasAcrescimoNovaPage />),
                  },
                  {
                    path: '/crm/faixas-acrescimo/historico',
                    handle: { breadcrumb: 'Histórico' },
                    element: protegida(['desconto_acrescimo:ver'], <FaixasAcrescimoHistoricoPage />),
                  },
                  { path: '/config/catalogo', element: <Navigate to="/crm/catalogo" replace /> },
                  { path: '/config/catalogo/planos/novo', element: <Navigate to="/crm/cotacao/planos/novo" replace /> },
                  { path: '/config/catalogo/planos/:id', element: <RedirectPlano /> },
                  { path: '/config/captacao', element: <Navigate to="/crm/captacao" replace /> },
                  {
                    path: '/crm/captacao',
                    element: protegida(['captacao:ver'], <CaptacaoPage />),
                  },
                  {
                    path: '/crm/simulador',
                    element: protegida(['crm:criar', 'catalogo:ver'], <SimuladorCrmPage />),
                  },
                  {
                    path: '/crm/documentos',
                    element: protegida(['crm:gerenciar'], <PorteiraDocumentos><DocumentosListaPage /></PorteiraDocumentos>),
                  },
                  {
                    path: '/crm/documentos/minhas-assinaturas',
                    element: protegida(['crm:ver'], <PorteiraDocumentos><DocumentosMinhasAssinaturasPage /></PorteiraDocumentos>),
                  },
                  {
                    path: '/crm/documentos/representante',
                    element: protegida(['crm:gerenciar'], <PorteiraDocumentos><DocumentosRepresentantePage /></PorteiraDocumentos>),
                  },
                  {
                    path: '/crm/documentos/emitidos/:id',
                    element: protegida(['crm:ver'], <PorteiraDocumentos><DocumentoEmitidoPage /></PorteiraDocumentos>),
                  },
                  {
                    path: '/crm/documentos/:id',
                    element: protegida(['crm:gerenciar'], <PorteiraDocumentos><DocumentosModeloPage /></PorteiraDocumentos>),
                  },
                  {
                    // #611 — destino do aviso de configuração pendente da faixa
                    // "Hoje" e do sino. Fica em /config junto das integrações
                    // bancárias, que é a outra config de cobrança; catálogo e
                    // captação saíram daqui por serem funcionalidade do CRM.
                    path: '/config/cobranca',
                    element: withSuspense(<ConfiguracaoCobrancaPage />),
                  },
                  {
                    path: '/config/estacao-setores',
                    element: withSuspense(<SetoresConfigPage />),
                  },
                  {
                    path: '/config/respostas-prontas',
                    element: withSuspense(<RespostasProntasConfigPage />),
                  },
                  {
                    path: '/config/canal-atendimento',
                    element: withSuspense(<CanalCredenciaisPage />),
                  },
                  {
                    path: '/config/integracoes-bancarias',
                    element: withSuspense(<IntegracoesBancariasPage />),
                  },
                  {
                    path: '/config/cobranca-cartao',
                    element: protegida(['cobranca_cartao:ver'], <ConfigGetnetPage />),
                  },
                  {
                    path: '/perfil',
                    element: withSuspense(<PerfilPage />),
                  },
                  // Admin de usuários — acesso restrito a role 'admin'
                  {
                    element: <RequireRole roles={['admin']} />,
                    children: [
                      {
                        path: '/admin',
                        element: withSuspense(<AdminPage />),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // Acesso negado
  {
    path: '/sem-acesso',
    element: <ForbiddenPage />,
  },

  // 404 — captura tudo
  {
    path: '*',
    errorElement: <RouteErrorBoundary />,
    loader: (): never => {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw new Response('Not Found', { status: 404 })
    },
    element: null,
  },
]

export { routes }

const router = createBrowserRouter(routes, {
  future: {
    v7_normalizeFormMethod: true,
  },
})

export function AppRouter() {
  return <RouterProvider router={router} />
}
